import React, { useState, useEffect, useMemo } from "react";
import { Offcanvas, Button, Form, Modal, Row, Col, Nav, Badge } from 'react-bootstrap';
import { getFirestore, collection, addDoc, updateDoc, doc, onSnapshot, serverTimestamp, deleteDoc, query, orderBy, limit, getDoc, setDoc } from 'firebase/firestore';
import { FaTools, FaDoorOpen, FaBeer, FaVideo, FaChair, FaTrash, FaEdit, FaHistory, FaMusic, FaBoxOpen, FaDollarSign, FaListUl, FaChartLine, FaSearch, FaUser, FaInfoCircle, FaPlus, FaCalendarDay, FaArchive, FaInbox, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { spinner } from "../utils/Helper";
import './EventSuite.css';

const DRINK_OPTIONS = ["Full open bar", "Drink Tickets", "Limited Open Bar", "Guests Pay"];

const sanitizeId = (name) => name ? name.replace(/\//g, '_').replace(/ /g, '_') : "";

const formatTime = (timeStr) => {
    if (!timeStr) return "";
    let [hours, minutes] = timeStr.split(':');
    let ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${ampm}`;
};

// --- HELPER SUB-COMPONENTS ---

const HistoryFeed = ({ eventId }) => {
    const [history, setHistory] = useState([]);
    const db = getFirestore();

    useEffect(() => {
        const q = query(collection(db, "event_requests", eventId, "history"), orderBy("timestamp", "desc"), limit(10));
        return onSnapshot(q, (snap) => {
            setHistory(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
    }, [eventId, db]);

    if (history.length === 0) return null;

    return (
        <div className="mt-3 p-2 rounded bg-black bg-opacity-40 border border-secondary border-opacity-50">
            <div className="meta-label mb-2 d-flex align-items-center text-info fw-bold" style={{ fontSize: '10px' }}>
                <FaHistory className="me-1" size={10} /> RECENT ACTIVITY
            </div>
            {history.map(item => (
                <div key={item.id} className="history-item mb-1 pb-1 border-bottom border-secondary border-opacity-25" style={{ fontSize: '11px', color: '#ffffff' }}>
                    <span className="text-info fw-bold">{item.updatedBy}</span>
                    <span className="ms-1 opacity-75" style={{ fontSize: '10px' }}>{item.action}</span>
                </div>
            ))}
        </div>
    );
};

const InfoFields = ({ formData, setFormData, checkMemberStatus, theme, isEdit }) => {
    const isLightMode = !isEdit && theme === 'light';
    const labelStyle = { color: isLightMode ? '#0dcaf0' : '#ffffff', fontWeight: '800' };
    const inputClass = isLightMode ? "form-control light-mode-input" : "form-control glass-input";

    return (
        <>
            <Row className="mb-4">
                <Col md={4}><Form.Label className="meta-label" style={labelStyle}>REQUESTOR NAME *</Form.Label><Form.Control type="text" placeholder="John Doe" value={formData.requestorName} required onChange={e => setFormData({ ...formData, requestorName: e.target.value })} className={inputClass} /></Col>
                <Col md={4}><Form.Label className="meta-label" style={labelStyle}>EMAIL ADDRESS *</Form.Label><Form.Control type="email" placeholder="email@example.com" value={formData.email} required onChange={e => setFormData({ ...formData, email: e.target.value })} onBlur={(e) => checkMemberStatus(e.target.value)} className={inputClass} /></Col>
                <Col md={4}><Form.Label className="meta-label" style={labelStyle}>OCCASION *</Form.Label><Form.Control type="text" placeholder="Birthday Party" value={formData.occasion} required onChange={e => setFormData({ ...formData, occasion: e.target.value })} className={inputClass} /></Col>
            </Row>
            <Row className="mb-4">
                <Col md={3}><Form.Label className="meta-label" style={labelStyle}>DATE *</Form.Label><Form.Control type="date" value={formData.date} required onChange={e => setFormData({ ...formData, date: e.target.value })} className={inputClass} /></Col>
                <Col md={3}><Form.Label className="meta-label" style={labelStyle}>START TIME *</Form.Label><Form.Control type="time" value={formData.startTime} required onChange={e => setFormData({ ...formData, startTime: e.target.value })} className={inputClass} /></Col>
                <Col md={3}><Form.Label className="meta-label" style={labelStyle}>END TIME *</Form.Label><Form.Control type="time" value={formData.endTime} required onChange={e => setFormData({ ...formData, endTime: e.target.value })} className={inputClass} /></Col>
                <Col md={3}><Form.Label className="meta-label" style={labelStyle}>GUESTS</Form.Label><Form.Control type="number" value={formData.expectedGuests} onChange={e => setFormData({ ...formData, expectedGuests: parseInt(e.target.value) || 0 })} className={inputClass} /></Col>
            </Row>
            <Row className="mb-4">
                <Col md={12}>
                    <Form.Label className="meta-label" style={labelStyle}><FaBeer className="me-2" /> DRINK OPTIONS</Form.Label>
                    <Form.Select value={formData.drinkOption} onChange={e => setFormData({ ...formData, drinkOption: e.target.value })} className={inputClass}>
                        {DRINK_OPTIONS.map(o => <option key={o}>{o}</option>)}
                    </Form.Select>
                </Col>
            </Row>
        </>
    );
};

const RoomFields = ({ formData, setFormData, theme, isEdit, pricingData }) => {
    const isLightMode = !isEdit && theme === 'light';
    const headingStyle = { color: '#0dcaf0', fontWeight: '800' };
    const labelClass = isLightMode ? "text-dark fw-bold" : "text-white fw-bold";

    // Dynamic Room Options based on Firestore pricing DB
    const roomOptions = pricingData.filter(p => p.type === 'room').map(p => p.label || p.id);

    return (
        <>
            <h6 style={headingStyle} className="mb-3"><FaDoorOpen className="me-2" /> ROOMS REQUESTED *</h6>
            <Row className="mb-4 g-2">
                {roomOptions.map(r => (
                    <Col xs={6} key={r}>
                        <div className={`p-2 rounded border transition-all ${formData.roomsRequested.includes(r) ? (isLightMode ? 'border-info bg-info bg-opacity-10 shadow-sm' : 'border-info bg-info bg-opacity-10 shadow-glow') : (isLightMode ? 'border-dark border-opacity-25' : 'border-secondary border-opacity-50')}`}>
                            <Form.Check
                                type="checkbox"
                                label={r}
                                className={labelClass}
                                checked={formData.roomsRequested.includes(r)}
                                onChange={() => {
                                    const updated = formData.roomsRequested.includes(r) ? formData.roomsRequested.filter(x => x !== r) : [...formData.roomsRequested, r];
                                    setFormData({ ...formData, roomsRequested: updated });
                                }}
                            />
                        </div>
                    </Col>
                ))}
            </Row>
        </>
    );
};

const EquipmentFields = ({ formData, setFormData, pricingData, handleEquipChange, renderEquipInput, theme, isEdit }) => {
    const isLightMode = !isEdit && theme === 'light';
    const headingStyle = { color: '#0dcaf0', fontWeight: '800' };
    const labelClass = isLightMode ? "text-dark fw-bold" : "text-white fw-bold";

    return (
        <>
            <h6 style={headingStyle} className="mb-3"><FaTools className="me-2" /> EQUIPMENT & SEATING</h6>
            <Row className="mb-4 g-3">
                <Col md={6}><div className={`p-3 rounded border d-flex justify-content-between align-items-center transition-all ${formData.projectorCount > 0 ? (isLightMode ? 'border-info bg-info bg-opacity-10 shadow-sm' : 'border-info bg-info bg-opacity-10 shadow-glow') : (isLightMode ? 'border-dark border-opacity-25' : 'border-secondary border-opacity-50')}`}><span className={labelClass}><FaVideo className="me-2" /> Projector</span>{renderEquipInput({ stock: 3, id: 'Projector' }, formData.projectorCount, (val) => setFormData({ ...formData, projectorCount: val }), isLightMode)}</div></Col>
                <Col md={6}><div className={`p-3 rounded border d-flex justify-content-between align-items-center transition-all ${formData.soundCount > 0 ? (isLightMode ? 'border-info bg-info bg-opacity-10 shadow-sm' : 'border-info bg-info bg-opacity-10 shadow-glow') : (isLightMode ? 'border-dark border-opacity-25' : 'border-secondary border-opacity-50')}`}><span className={labelClass}><FaMusic className="me-2" /> Sound System</span>{renderEquipInput({ stock: 1, id: 'PA_System' }, formData.soundCount, (val) => setFormData({ ...formData, soundCount: val }), isLightMode)}</div></Col>
                <Col md={6}><div className={`p-3 rounded border d-flex justify-content-between align-items-center transition-all ${formData.chairCount > 0 ? (isLightMode ? 'border-info bg-info bg-opacity-10 shadow-sm' : 'border-info bg-info bg-opacity-10 shadow-glow') : (isLightMode ? 'border-dark border-opacity-25' : 'border-secondary border-opacity-50')}`}><span className={labelClass}><FaChair className="me-2" />Chairs:</span><Form.Control type="number" size="sm" style={{ 'width': "70px" }} value={formData.chairCount} className={isLightMode ? "form-control light-mode-input border-0" : "form-control glass-input border-0"} onChange={(e) => setFormData({ ...formData, chairCount: parseInt(e.target.value) || 0 })} /></div></Col>
                {pricingData.filter(p => p.type === 'equipment' && !["Projector", "Chairs", "PA_System", "Tax_Service_Fee"].includes(p.id)).map(item => (
                    <Col md={6} key={item.id}><div className={`p-3 rounded border d-flex justify-content-between align-items-left transition-all ${formData.equipmentSelections?.[item.id] > 0 ? (isLightMode ? 'border-info bg-info bg-opacity-10 shadow-sm' : 'border-info bg-info bg-opacity-10 shadow-glow') : (isLightMode ? 'border-dark border-opacity-25' : 'border-secondary border-opacity-50')}`}><span className={labelClass}><FaBoxOpen className="me-2" /><span className={` ${labelClass}`}>{item.label}</span></span>{renderEquipInput(item, formData.equipmentSelections?.[item.id], (val) => handleEquipChange(item.id, val, item.stock), isLightMode)}</div></Col>
                ))}
            </Row>
        </>
    );
};

const CreateEventRequest = ({ theme, show, onHide, editData = null, pricingData = [] }) => {
    const isLightMode = !editData && theme === 'light';
    const [step, setStep] = useState(1);
    const [activeTab, setActiveTab] = useState('info');

    const initialFormState = {
        requestorName: '', email: '', occasion: '', date: '', startTime: '', endTime: '',
        roomsRequested: [], drinkOption: 'Guests Pay',
        projectorCount: 0, chairCount: 0,
        soundCount: 0, expectedGuests: 0,
        equipmentSelections: {}, isMember: false
    };
    const [formData, setFormData] = useState(initialFormState);

    useEffect(() => {
        if (show) {
            if (editData) {
                setFormData({ ...initialFormState, ...editData });
                setActiveTab('info');
            } else {
                setFormData(initialFormState);
                setStep(1);
            }
        }
    }, [show, editData]);

    const checkMemberStatus = async (email) => {
        if (!email) return;
        const db = getFirestore();
        try {
            const profileRef = doc(db, "profiles", email.toLowerCase().trim());
            const profileSnap = await getDoc(profileRef);
            setFormData(prev => ({ ...prev, isMember: profileSnap.exists() }));
        } catch (err) { console.error("Member lookup error:", err); }
    };

    const calculateTotal = () => {
        let subtotal = 0;
        let hours = 1;
        if (formData.startTime && formData.endTime) {
            const start = new Date(`2000-01-01T${formData.startTime}`);
            const end = new Date(`2000-01-01T${formData.endTime}`);
            hours = Math.max(1, (end - start) / (1000 * 60 * 60));
        }
        formData.roomsRequested.forEach(roomName => {
            const targetId = sanitizeId(roomName);
            // Dynamic look up from the pricingData array provided by Firestore
            const item = pricingData.find(p => p.id === targetId || p.label === roomName);
            if (item) subtotal += (Number(item.flatRate) || 0) + (Number(item.hourlyRate) || 0) * hours;
        });
        if (formData.projectorCount > 0) {
            const proj = pricingData.find(p => p.id === "Projector");
            if (proj) subtotal += ((Number(proj.flatRate) || 0) + (Number(proj.hourlyRate) || 0) * hours) * formData.projectorCount;
        }
        if (formData.soundCount > 0) {
            const pa = pricingData.find(p => p.id === "PA_System");
            if (pa) subtotal += ((Number(pa.flatRate) || 0) + (Number(pa.hourlyRate) || 0) * hours) * formData.soundCount;
        }
        if (formData.chairCount > 0) {
            const chair = pricingData.find(p => p.id === "Chairs");
            subtotal += (formData.chairCount * (Number(chair?.perUnit) || 0)) + (Number(chair?.flatRate) || 0);
        }
        Object.entries(formData.equipmentSelections || {}).forEach(([itemId, qty]) => {
            const item = pricingData.find(p => p.id === itemId);
            if (item && qty > 0) subtotal += ((Number(item.flatRate) || 0) + (Number(item.perUnit) || 0)) * Number(qty);
        });
        return subtotal.toFixed(2);
    };

    const handleEquipChange = (id, qty, maxStock) => {
        const value = parseInt(qty) || 0;
        if (maxStock > 0 && value > maxStock) return toast.warning(`Only ${maxStock} available.`);
        setFormData(prev => ({ ...prev, equipmentSelections: { ...prev.equipmentSelections, [id]: value } }));
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        spinner(true);
        try {
            const db = getFirestore();

            const dynamicTasks = {};
            formData.roomsRequested.forEach(room => dynamicTasks[`${room} Prepped`] = false);
            if (formData.drinkOption !== "Guests Pay") dynamicTasks["Bar Service Set"] = false;
            if (formData.projectorCount > 0) dynamicTasks["Projector Ready"] = false;
            if (formData.soundCount > 0) dynamicTasks["PA System Ready"] = false;
            if (formData.chairCount > 0) dynamicTasks["Seating Arranged"] = false;

            Object.entries(formData.equipmentSelections || {}).forEach(([itemId, qty]) => {
                if (qty > 0) {
                    const item = pricingData.find(p => p.id === itemId);
                    const label = item ? item.label : itemId.replace(/_/g, ' ');
                    dynamicTasks[`${label} Ready`] = false;
                }
            });

            let finalTasks = dynamicTasks;
            if (editData?.tasks) {
                finalTasks = { ...dynamicTasks };
                Object.keys(dynamicTasks).forEach(taskKey => {
                    if (editData.tasks.hasOwnProperty(taskKey)) {
                        finalTasks[taskKey] = editData.tasks[taskKey];
                    }
                });
            }

            const submissionData = {
                ...formData,
                tasks: finalTasks,
                totalQuote: calculateTotal(),
                timestamp: serverTimestamp()
            };

            if (editData?.id) await updateDoc(doc(db, "event_requests", editData.id), submissionData);
            else await addDoc(collection(db, "event_requests"), submissionData);
            onHide();
            toast.success("Success!");
        } catch (err) { toast.error(err.message); }
        finally { spinner(false); }
    };

    const renderEquipInput = (item, currentVal, onChangeHandler, isLight) => {
        const stock = Number(item.stock) || 0;
        return <Form.Control type="number" size="sm" min="0" max={stock || undefined} value={currentVal || 0} className={isLight ? "form-control light-mode-input ms-2" : "form-control glass-input ms-2"} style={{ width: '70px' }} onChange={(e) => onChangeHandler(parseInt(e.target.value) || 0)} />;
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            centered
            contentClassName={isLightMode ? 'bg-white text-muted shadow-lg' : 'bg-dark text-white shadow-lg'}
            backdropClassName={isLightMode ? 'light-modal-backdrop' : 'adminBackdrop'}
        >
            <Modal.Header closeButton className={isLightMode ? '' : 'btn-close-white'}>
                <Modal.Title className={isLightMode ? "orange fw-bold" : "text-info fw-bold"}>{editData ? 'Edit Event' : 'Request Event'}</Modal.Title>
            </Modal.Header>

            <h4 className={isLightMode ? "apc-text-black animated fadeInLeft mx-3" : "apc-text animated fadeInLeft mx-3"}>Albuquerque Press Club</h4>
            <Modal.Body className="p-4 custom-scrollbar" style={{ maxHeight: '85vh', overflowY: 'auto' }}>
                {!editData ? (
                    <div>
                        <div className="d-flex mb-4 justify-content-left gap-3">
                            <Badge bg={step >= 1 ? (isLightMode ? "info" : "info") : "secondary"}>1. Info</Badge>
                            <Badge bg={step >= 2 ? (isLightMode ? "info" : "info") : "secondary"}>2. Rooms</Badge>
                            <Badge bg={step >= 3 ? (isLightMode ? "info" : "info") : "secondary"}>3. Equipment</Badge>
                        </div>
                        {step === 1 && <InfoFields formData={formData} setFormData={setFormData} checkMemberStatus={checkMemberStatus} theme={theme} isEdit={false} />}
                        {step === 2 && <RoomFields formData={formData} setFormData={setFormData} theme={theme} isEdit={false} pricingData={pricingData} />}
                        {step === 3 && <EquipmentFields formData={formData} setFormData={setFormData} pricingData={pricingData} handleEquipChange={handleEquipChange} renderEquipInput={renderEquipInput} theme={theme} isEdit={false} />}
                        <div className="d-flex justify-content-between mt-4">
                            {step > 1 && <Button variant="secondary" onClick={() => setStep(step - 1)}>Back</Button>}
                            <div className="ms-auto">
                                {step < 3 ? <Button variant="info" className="fw-bold text-white" onClick={() => setStep(step + 1)}>Next</Button> : <Button variant="success" className="fw-bold" onClick={handleSubmit}>Submit Request</Button>}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="d-flex justify-content-left w-100">
                        <Nav variant="pills" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4 custom-responsive-nav-pills">
                            <Nav.Item><Nav.Link eventKey="info" className="nav-link custom-pill"><FaInfoCircle className="me-2" />Info</Nav.Link></Nav.Item>
                            <Nav.Item><Nav.Link eventKey="rooms" className="nav-link custom-pill"><FaDoorOpen className="me-2" />Rooms</Nav.Link></Nav.Item>
                            <Nav.Item><Nav.Link eventKey="equipment" className="nav-link custom-pill"><FaTools className="me-2" />Equipment</Nav.Link></Nav.Item>
                        </Nav>
                    </div>
                )}

                {editData && (
                    <div className="mt-2 animated fadeIn">
                        {activeTab === 'info' && <InfoFields formData={formData} setFormData={setFormData} checkMemberStatus={checkMemberStatus} theme="dark" isEdit={true} />}
                        {activeTab === 'rooms' && <RoomFields formData={formData} setFormData={setFormData} theme="dark" isEdit={true} pricingData={pricingData} />}
                        {activeTab === 'equipment' && <EquipmentFields formData={formData} setFormData={setFormData} pricingData={pricingData} handleEquipChange={handleEquipChange} renderEquipInput={renderEquipInput} theme="dark" isEdit={true} />}
                    </div>
                )}

                <div className={`mt-4 p-4 rounded text-end border ${isLightMode ? 'bg-light border-dark border-opacity-10' : 'bg-secondary bg-opacity-10 border-secondary border-opacity-25'}`}>
                    <h2 className={`m-0 fw-bold ${isLightMode ? 'text-dark' : 'text-info'}`}>${calculateTotal()}</h2>
                    {editData && <Button onClick={handleSubmit} variant="info" className="w-100 text-white fw-bold py-3 mt-2">Update Event</Button>}
                </div>
            </Modal.Body>

            <style>{`
                .light-mode-input {
                    background-color: #ffffff !important;
                    border: 1.2px solid #88888863 !important;
                    color: #212529 !important;
                    font-weight: 600 !important;
                }
                .light-mode-input::placeholder { color: #f88634 !important; opacity: 1; }
                .light-mode-input:focus { border-color: #0dcaf0 !important; outline: 1px solid #0dcaf0; }
                
                .meta-label { 
                    display: block !important; 
                    margin-bottom: 6px !important;
                    font-size: 11px !important;
                    letter-spacing: 0.8px !important;
                }
                .light-modal-backdrop {
                    background-color: rgba(255, 255, 255, 0.7) !important;
                    backdrop-filter: blur(5px);
                    opacity: 1 !important;
                }
                .nav-pills .nav-link.custom-pill {
                    background-color: rgba(255, 255, 255, 0.1) !important;
                    color: #0dcaf0 !important;
                    border: 1px solid rgba(255, 255, 255, 0.1) !important;
                    padding: 10px 15px !important; 
                    font-weight: bold;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 4px;
                    transition: all 0.2s ease;
                }
                .nav-pills .nav-link.custom-pill.active {
                    background-color: #0dcaf0 !important;
                    color: #fff !important;
                    box-shadow: 0 4px 15px rgba(13, 202, 240, 0.5) !important;
                    border: 1px solid #0dcaf0 !important;
                }
                @media (min-width: 768px) {
                    .custom-responsive-nav-pills { flex-direction: row !important; }
                    .custom-pill { margin: 0 5px !important; min-width: 120px; }
                }
                @media (max-width: 767px) {
                    .custom-responsive-nav-pills { flex-direction: column !important; width: 100%; }
                    .custom-pill { margin: 2px 0; text-align: center; width: 100%; }
                }
            `}</style>
        </Modal>
    );
};

export default function EventSuite({ theme, isAdmin }) {
    const db = getFirestore();
    const [showCreate, setShowCreate] = useState(false);
    const [showAdmin, setShowAdmin] = useState(false);
    const [events, setEvents] = useState([]);
    const [editData, setEditData] = useState(null);
    const [pricing, setPricing] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showTodayOnly, setShowTodayOnly] = useState(false);
    const [adminView, setAdminView] = useState('events');
    const [isDataInitialized, setIsDataInitialized] = useState(false);

    // Initialization logic for backend listeners
    const initBackendListeners = () => {
        if (isDataInitialized) return;

        spinner(true);
        const unsubEvents = onSnapshot(collection(db, "event_requests"), (snap) => {
            setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setIsDataInitialized(true);
            spinner(false);
        });

        const unsubPricing = onSnapshot(collection(db, "pricing"), (snap) => {
            setPricing(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.type === 'room' ? -1 : 1)));
            setIsDataInitialized(true);
            spinner(false);
        });

        // Store cleanup functions in local variables to be returned by useEffect cleanup
        window._eventUnsub = unsubEvents;
        window._pricingUnsub = unsubPricing;
    };

    useEffect(() => {
        // Cleanup on unmount
        return () => {
            if (window._eventUnsub) window._eventUnsub();
            if (window._pricingUnsub) window._pricingUnsub();
        };
    }, []);

    const handleAdminClick = () => {
        initBackendListeners();
        setShowAdmin(true);
    };

    const handleRequestClick = () => {
        initBackendListeners();
        setShowCreate(true);
    };

    const handleUpdateRateField = async (id, field, value) => {
        try {
            await updateDoc(doc(db, "pricing", id), { [field]: Number(value) });
            toast.success("Rate Updated");
        } catch (err) { toast.error("Update Failed"); }
    };

    const handleSaveRate = async (rateData) => {
        spinner(true);
        try {
            const rateId = rateData.id || sanitizeId(rateData.label);
            await setDoc(doc(db, "pricing", rateId), { ...rateData, id: rateId });
            toast.success("Rate added!");
        } catch (err) { toast.error("Save failed"); }
        finally { spinner(false); }
    };

    const handleDeleteRate = async (id) => {
        if (window.confirm(`Delete ${id}?`)) {
            await deleteDoc(doc(db, "pricing", id));
        }
    };

    const toggleArchive = async (event, currentVal) => {
        try {
            const userEmail = localStorage.getItem('email') || "Staff";
            await updateDoc(doc(db, "event_requests", event.id), { isArchived: !currentVal });

            await addDoc(collection(db, "event_requests", event.id, "history"), {
                updatedBy: userEmail,
                action: !currentVal ? 'Archived Event' : 'Restored Event',
                timestamp: serverTimestamp()
            });

            toast.info(!currentVal ? "Event Archived" : "Event Restored");
        } catch (err) { toast.error(err.message); }
    };

    const toggleTask = async (event, taskName, currentVal) => {
        try {
            const userEmail = localStorage.getItem('email') || "Staff";
            const taskRef = doc(db, "event_requests", event.id);
            await updateDoc(taskRef, { [`tasks.${taskName}`]: !currentVal });

            await addDoc(collection(db, "event_requests", event.id, "history"), {
                updatedBy: userEmail,
                action: `${!currentVal ? 'Checked' : 'Unchecked'} ${taskName}`,
                timestamp: serverTimestamp()
            });

            toast.info("Task Updated");
        } catch (err) { toast.error(err.message); }
    };

    const filteredEvents = events.filter(e => {
        const term = searchTerm.toLowerCase().trim();
        const today = new Date().toISOString().split('T')[0];
        const viewMatch = adminView === 'archive' ? e.isArchived === true : !e.isArchived;

        const matchesSearch = !term || (
            (e.occasion && e.occasion.toLowerCase().includes(term)) ||
            (e.requestorName && e.requestorName.toLowerCase().includes(term)) ||
            (e.email && e.email.toLowerCase().includes(term)) ||
            (e.date && e.date.includes(term))
        );

        const matchesToday = !showTodayOnly || (e.date === today);

        return viewMatch && matchesSearch && matchesToday;
    }).sort((a, b) => new Date(b.date) - new Date(a.date));

    return (
        <>
            <div className="d-flex gap-2">
                {!isAdmin && <Button onClick={handleRequestClick} className="btn-outline-info btn btn-primary"><span className="hoverX">Request Event</span></Button>}
                {isAdmin && <Button onClick={handleAdminClick} className="hoverX shadow-sm"><span className="circle-btn centerCircle">🗓️</span>Events</Button>}
            </div>

            <CreateEventRequest theme={theme} show={showCreate} editData={editData} pricingData={pricing} onHide={() => { setShowCreate(false); setEditData(null); }} />

            <Offcanvas show={showAdmin} onHide={() => setShowAdmin(false)} placement="end" size="lg" backdropClassName="adminBackdrop" className="bg-dark shadow-lg">
                <Offcanvas.Header closeButton className=" py-3 border-bottom border-secondary border-opacity-25">
                    <h4 className="modified xTitle animated rollIn"><span>Events</span></h4>
                </Offcanvas.Header>
                <div className="w-100 d-flex flex-column align-items-center mt-2">
                    <Nav variant="pills" activeKey={adminView} onSelect={(k) => setAdminView(k)} className="border-0 d-flex flex-row justify-content-center w-100 mb-2 p-2">
                        <Nav.Item><Nav.Link eventKey="events" className="nav-link custom-pill p-2"><FaListUl className="me-2 text-white" />Events</Nav.Link></Nav.Item>
                        <Nav.Item><Nav.Link eventKey="archive" className="nav-link custom-pill p-2"><FaArchive className="me-2 text-white" />Archive</Nav.Link></Nav.Item>
                        <Nav.Item><Nav.Link eventKey="pricing" className="nav-link custom-pill p-2"><FaDollarSign className="me-2 text-white" />Rates</Nav.Link></Nav.Item>
                    </Nav>
                </div>
                <Offcanvas.Body className="custom-scrollbar p-3">
                    {(adminView === 'events' || adminView === 'archive') ? (
                        <div className="animate-fade-in text-center">
                            <div className="d-flex justify-content-center gap-2 mb-4">
                                {adminView === 'events' && (
                                    <Badge bg="success" className="py-2 px-3 rounded-pill shadow-sm d-inline-flex align-items-center"><FaChartLine className="me-2" /> Potential Revenue: ${filteredEvents.reduce((sum, e) => sum + (parseFloat(e.totalQuote) || 0), 0).toFixed(2)}</Badge>
                                )}
                                <Badge bg="info" className="py-2 px-3 rounded-pill shadow-sm d-inline-flex align-items-center"><FaListUl className="me-2" /> Found: {filteredEvents.length}</Badge>
                            </div>

                            <div className="d-flex flex-column gap-3 mb-4">
                                <div className="d-flex justify-content-between align-items-center px-2">
                                    <Form.Check
                                        type="switch"
                                        id="today-toggle"
                                        label={<span className={`small fw-bold transition-all ${showTodayOnly ? 'text-info' : 'text-white opacity-50'}`}><FaCalendarDay className="me-2" /> SHOW TODAY'S EVENTS ONLY</span>}
                                        checked={showTodayOnly}
                                        onChange={() => setShowTodayOnly(!showTodayOnly)}
                                        className="custom-switch-info"
                                    />
                                    {(searchTerm || showTodayOnly) && (
                                        <Button variant="link" className="text-info p-0 text-decoration-none small fw-bold" onClick={() => { setSearchTerm(''); setShowTodayOnly(false); }}>
                                            <FaTimes className="me-1" /> CLEAR FILTERS
                                        </Button>
                                    )}
                                </div>
                                <div className="search-container"><Form.Control placeholder="Search occasion, name, email, or date (YYYY-MM-DD)..." className="search-input py-2" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /><FaSearch className="search-icon-inside" /></div>
                            </div>

                            {filteredEvents.map(event => (
                                <div key={event.id} className="management-card mb-4 p-4 rounded bg-black bg-opacity-40 border border-secondary border-opacity-50 shadow-lg text-start">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div>
                                            <h5 className="text-info fw-bold mb-1" style={{ color: '#0dcaf0' }}>{event.occasion}</h5>
                                            <div className="small fw-bold text-white mb-1"><FaUser className="me-2" size={10} />{event.requestorName}</div>
                                            <div className="small fw-bold text-white">{event.date} | {formatTime(event.startTime)} | {event.expectedGuests} Guests</div>
                                            <div className="text-success fw-bold mt-2" style={{ fontSize: '15px' }}><span className="text-white opacity-75" style={{ fontSize: '10px' }}>QUOTE: </span>${event.totalQuote}</div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <button className="circle-btn centerCircle shadow-sm" onClick={() => { setEditData(event); setShowCreate(true); }}><FaEdit className="text-info" size={12} /></button>
                                            <button className="circle-btn centerCircle shadow-sm" onClick={() => toggleArchive(event, event.isArchived)} title={event.isArchived ? "Restore" : "Archive"}>
                                                {event.isArchived ? <FaInbox className="text-warning" size={12} /> : <FaArchive className="text-warning" size={12} />}
                                            </button>
                                            <button className="circle-btn centerCircle btn-delete shadow-sm" onClick={() => { if (window.confirm('Permanent Delete?')) deleteDoc(doc(db, "event_requests", event.id)) }}><FaTrash size={12} /></button>
                                        </div>
                                    </div>
                                    <hr className="opacity-25 my-3 border-secondary" />
                                    <div className="row g-2">
                                        {event.tasks && Object.entries(event.tasks).map(([taskName, isDone]) => (
                                            <Col xs={6} key={taskName} className={`checklist-item p-2 rounded border d-flex justify-content-between align-items-center transition-all ${isDone ? 'bg-success bg-opacity-20 border-success text-success shadow-glow' : 'bg-black bg-opacity-20 border-secondary border-opacity-50 text-white'}`}>
                                                <span className="small fw-bold text-capitalize" style={{ fontSize: '10px' }}>{taskName}</span>
                                                <Form.Check type="switch" checked={isDone} onChange={() => toggleTask(event, taskName, isDone)} />
                                            </Col>
                                        ))}
                                    </div>
                                    <HistoryFeed eventId={event.id} />
                                </div>
                            ))}
                            {filteredEvents.length === 0 && <div className="text-white opacity-25 py-5"><FaInbox size={48} className="mb-3 d-block mx-auto" />No events found</div>}
                        </div>
                    ) : (
                        <div className="animate-fade-in p-1">
                            <h5 className="text-info fw-bold mb-4 d-flex align-items-center justify-content-center"><FaDollarSign className="me-2" />Pricing & Rates</h5>
                            <div className="management-card mb-4 p-3 rounded border border-info border-opacity-25 bg-black bg-opacity-40 shadow-sm">
                                <h6 className="text-info fw-bold mb-3 d-flex align-items-center" style={{ fontSize: '14px' }}><FaPlus className="me-2" size={12} /> ADD NEW ITEM OR ROOM</h6>
                                <Form onSubmit={(e) => {
                                    e.preventDefault();
                                    const formData = new FormData(e.target);
                                    handleSaveRate({
                                        label: formData.get('label'),
                                        type: formData.get('type'),
                                        flatRate: Number(formData.get('flatRate')) || 0,
                                        hourlyRate: Number(formData.get('hourlyRate')) || 0,
                                        perUnit: Number(formData.get('perUnit')) || 0,
                                        stock: Number(formData.get('stock')) || 0
                                    });
                                    e.target.reset();
                                }}>
                                    <Row className="g-2">
                                        <Col md={8}><Form.Control name="label" placeholder="Item Name" size="sm" className="glass-input mb-2" required /></Col>
                                        <Col md={4}><Form.Select name="type" size="sm" className="glass-input mb-2"><option value="equipment">Equipment</option><option value="room">Room</option></Form.Select></Col>
                                        <Col xs={3}><label className="meta-label text-white-50 w-100" style={{ fontSize: '9px' }}>Flat</label><Form.Control name="flatRate" size="sm" type="number" placeholder="0" className="glass-input text-center" /></Col>
                                        <Col xs={3}><label className="meta-label text-white-50 w-100" style={{ fontSize: '9px' }}>Hourly</label><Form.Control name="hourlyRate" size="sm" type="number" placeholder="0" className="glass-input text-center" /></Col>
                                        <Col xs={3}><label className="meta-label text-white-50 w-100" style={{ fontSize: '9px' }}>Unit</label><Form.Control name="perUnit" size="sm" type="number" placeholder="0" className="glass-input text-center" /></Col>
                                        <Col xs={3}><label className="meta-label text-white-50 w-100" style={{ fontSize: '9px' }}>Stock</label><Form.Control name="stock" size="sm" type="number" placeholder="1" className="glass-input text-center" /></Col>
                                        <Col xs={12} className="mt-3"><Button type="submit" variant="info" size="sm" className="w-100 text-white fw-bold py-2">SAVE NEW RATE</Button></Col>
                                    </Row>
                                </Form>
                            </div>
                            <div className="rates-container">
                                {pricing.map((item) => (
                                    <div key={item.id} className="management-card mb-3 p-3 rounded bg-black bg-opacity-40 border border-secondary border-opacity-50 shadow-sm">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="fw-bold text-info">{item.label || item.id}</span>
                                            <div className="ms-auto"><button onClick={() => handleDeleteRate(item.id)} className="circle-btn centerCircle btn-delete shadow-sm"><FaTrash size={10} /></button></div>
                                        </div>
                                        <Row className="g-2 text-center">
                                            <Col xs={6} md={3}><label className="meta-label text-white-50 w-100" style={{ fontSize: '9px' }}>Flat Rate</label><Form.Control size="sm" type="number" defaultValue={item.flatRate} onBlur={(e) => handleUpdateRateField(item.id, 'flatRate', e.target.value)} className="glass-input text-center" /></Col>
                                            <Col xs={6} md={3}><label className="meta-label text-white-50 w-100" style={{ fontSize: '9px' }}>Hourly</label><Form.Control size="sm" type="number" defaultValue={item.hourlyRate} onBlur={(e) => handleUpdateRateField(item.id, 'hourlyRate', e.target.value)} className="glass-input text-center" /></Col>
                                            <Col xs={6} md={3}><label className="meta-label text-white-50 w-100" style={{ fontSize: '9px' }}>Per Unit</label><Form.Control size="sm" type="number" defaultValue={item.perUnit} onBlur={(e) => handleUpdateRateField(item.id, 'perUnit', e.target.value)} className="glass-input text-center" /></Col>
                                            <Col xs={6} md={3}><label className="meta-label text-white-50 w-100" style={{ fontSize: '9px' }}>Stock/Qty</label><Form.Control size="sm" type="number" defaultValue={item.stock} onBlur={(e) => handleUpdateRateField(item.id, 'stock', e.target.value)} className="glass-input text-center" /></Col>
                                        </Row>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Offcanvas.Body>
            </Offcanvas>

            <style>{`
                .dark-sidebar-pill {
                    background-color: rgba(255, 255, 255, 0.08) !important;
                    color: #0dcaf0 !important;
                    border: 1px solid rgba(211, 211, 211, 0.2) !important;
                }
                .dark-sidebar-pill.active {
                    background-color: #0dcaf0 !important;
                    color: #fff !important;
                    box-shadow: 0 0 15px rgba(13, 202, 240, 0.6) !important;
                }
                .custom-switch-info .form-check-input:checked {
                    background-color: #0dcaf0 !important;
                    border-color: #0dcaf0 !important;
                    box-shadow: 0 0 8px rgba(13, 202, 240, 0.5) !important;
                }
                .custom-switch-info .form-check-input {
                    background-color: rgba(255, 255, 255, 0.1) !important;
                    border-color: rgba(255, 255, 255, 0.2) !important;
                    cursor: pointer;
                }
            `}</style>
        </>
    );
}